// One row on the steering board. Priority-pip drives ordering at the
// bucket level (high/normal/low via `priority::pN` scoped labels). The
// row click + the edit pencil both open the editor where the user
// changes the priority via the editor's Priority segment.
//
// There's no within-bucket reorder: customers steer with the three
// priority buckets, not with slot positions. GitLab's `weight` field
// (the only native ordering hook) is Premium-only, and labels are for
// categories, not ordering — so finer-grained reorder would need a
// custom persistence layer for a UX nobody asked for.

import type { JSX } from "react";

import type { DashboardIssue, Priority } from "@loom/shared/dashboard";

import { useT, type MessageKey } from "../../i18n/index.js";

import { Avatar } from "./Avatar";
import { Icon } from "./Icon";

interface PriorityMeta {
  readonly labelKey: MessageKey;
  readonly color: string;
}

export const PRIORITY_META: Record<Priority, PriorityMeta> = {
  high: { labelKey: "priority.high", color: "var(--st-blocked)" },
  normal: { labelKey: "priority.normal", color: "var(--accent)" },
  low: { labelKey: "priority.low", color: "var(--ink-subtle)" },
};

interface SteerRowProps {
  readonly issue: DashboardIssue;
  readonly onEdit: (issue: DashboardIssue) => void;
  readonly onChat: (issue: DashboardIssue) => void;
}

export const SteerRow = ({
  issue,
  onEdit,
  onChat,
}: SteerRowProps): JSX.Element => {
  const t = useT();
  const prio = PRIORITY_META[issue.priority];
  const ready = issue.dor !== "fail";
  const verified = issue.dod === "pass";
  return (
    <div className="steer-row" onClick={() => onEdit(issue)}>
      <span
        className="steer-prio"
        style={{ background: prio.color }}
        title={t("dashboard.steerRow.priorityTitle", { priority: t(prio.labelKey) })}
      />
      <span className="steer-key">{issue.key}</span>
      <span className="steer-row-title">{issue.title}</span>
      {!ready && (
        <span className="steer-tag tag-needs">
          <Icon name="flag" size={9} /> {t("dashboard.steerRow.needsDetail")}
        </span>
      )}
      {verified && (
        <span className="steer-tag tag-ok">
          <Icon name="check" size={9} /> {t("dashboard.steerRow.verified")}
        </span>
      )}
      {issue.milestone && (
        <span
          className="steer-tag tag-milestone"
          title={t("dashboard.steerRow.milestoneTitle", { title: issue.milestone.title })}
          data-testid={`steer-row-milestone-${issue.iid}`}
        >
          <Icon name="sliders" size={9} /> {issue.milestone.title}
        </span>
      )}
      {issue.points !== null && (
        <span className="steer-pts">
          {issue.points}
          <small>{t("dashboard.steerRow.pt")}</small>
        </span>
      )}
      <Avatar handle={issue.assignee} size={20} />
      <button
        className="steer-chat"
        title={t("dashboard.steerRow.chatTitle")}
        aria-label={t("dashboard.steerRow.chatAria", { key: issue.key })}
        onClick={(e) => {
          e.stopPropagation();
          onChat(issue);
        }}
      >
        <Icon name="chat" size={13} />
      </button>
      <button
        className="steer-edit"
        title={t("dashboard.steerRow.editTitle")}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(issue);
        }}
      >
        <Icon name="pencil" size={13} />
      </button>
    </div>
  );
};
